import { describe, expect, it } from '@jest/globals';
import { checkMatch } from './checkMatch.js';
import type { PackageSettings } from '@ms-cloudpack/common-types';

describe('checkMatch', () => {
  // basic cases
  it.each<[expected: boolean, desc: string, match: PackageSettings['match']]>([
    [false, 'if string name does not match', 'nope'],
    [false, 'if object name does not match', { name: 'nope', version: '^1.0.0' }],
    [false, 'if object name matches but version is wrong', { name: 'test', version: '^2.0.0' }],
    [true, 'if string name matches', 'test'],
    [true, 'if object name matches (no version)', { name: 'test' }],
    [true, 'if object name and version match', { name: 'test', version: '^1.0.0' }],
    [true, 'for * (wildcard) string name', '*'],
    [true, 'for * (wildcard) object name (no version)', { name: '*' }],
    [true, 'for * (wildcard) object name and matching version', { name: '*', version: '^1.0.0' }],
    [true, 'if partial wildcard name matches', 'te*'],
    [false, 'if partial wildcard name does not match', 'n*'],
    [false, 'if wildcard is not at end of string', '*t'],
    [true, 'if any string in array matches', ['nope', 'test']],
    [true, 'if any object in array matches', [{ name: 'nope' }, { name: 'test' }]],
    [false, 'if nothing in array matches', ['nope', 'nope2']],
    [false, 'for empty array', []],
    [true, 'for negated non-matching string name', '!nope'],
    [false, 'for negated matching string name', '!test'],
    [false, 'for negated * (wildcard) string name', '!*'],
    [false, 'for negated matching partial wildcard string name', '!te*'],
    [true, 'for negated non-matching partial wildcard string name', '!a*'],
    [false, 'for negated matching name with version *', { name: '!test', version: '*' }],
    [false, 'for negated matching name in array (following matching name)', ['*', '!test']],
    // include all, exclude test, include t* => true
    [true, 'for array of matching, negated matching, matching again (re-include)', ['*', '!test', 't*']],
  ])('returns %s %s', (expected, _, match) => {
    const result = checkMatch({
      name: 'test',
      version: '1.2.3',
      match,
    });
    expect(result).toBe(expected);
  });

  // exactMatch cases
  it.each<[expected: boolean, desc: string, match: PackageSettings['match']]>([
    [true, 'if string name matches', 'test'],
    [true, 'if object name matches (no version)', { name: 'test' }],
    // the version match doesn't need to be exact
    [true, 'if object name and version range match', { name: 'test', version: '^1.0.0' }],
    [false, 'for * (wildcard)', '*'],
    [false, 'for partial wildcard', 'te*'],
  ])('with exactMatch, returns %s %s', (expected, _, match) => {
    const result = checkMatch({
      name: 'test',
      version: '1.2.3',
      match,
      exactMatch: true,
    });
    expect(result).toBe(expected);
  });

  // prerelease cases
  it.each<[expected: boolean, desc: string, match: PackageSettings['match']]>([
    [true, 'for matching prerelease range', { name: 'test', version: '^1.0.1-beta.0' }],
    [true, 'for version *', { name: 'test', version: '*' }],
    [false, 'for non-matching prerelease range', { name: 'test', version: '^1.0.1-beta.2' }],
    // This one is important to prevent unexpected behavior in the generated config for prerelease and hotfix versions
    [false, 'for non-prerelease range', { name: 'test', version: '^1.0.0' }],
  ])('with prerelease version, returns %s %s', (expected, _, match) => {
    const result = checkMatch({
      name: 'test',
      // the package has a prerelease version
      version: '1.0.1-beta.1',
      match,
    });
    expect(result).toBe(expected);
  });

  it('throws on negated name with a version', () => {
    expect(() =>
      checkMatch({
        name: 'test',
        version: '1.2.3',
        match: { name: '!test', version: '1.2.3' },
      }),
    ).toThrow('Negated match name "!test" cannot have a version ("1.2.3")');
  });
});
